import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const CONTACT_EMAIL = 'racu.mihai@gmail.com';

type Lang = 'ro' | 'ru' | 'en';

type Section = { h: string; p?: string[]; ul?: string[] };
type Doc = { title: string; updated: string; intro: string; sections: Section[] };

const CONTENT: Record<Lang, Doc> = {
  ro: {
    title: 'Politică de confidențialitate',
    updated: 'Ultima actualizare: 8 iulie 2026',
    intro:
      'Această politică de confidențialitate descrie modul în care aplicația Pnerp („aplicația”) colectează, utilizează și protejează datele personale ale utilizatorilor. Aplicația este un instrument intern destinat personalului serviciului silvic de stat din Republica Moldova pentru înregistrarea și gestionarea parcelelor forestiere și a fotografiilor georeferențiate.',
    sections: [
      {
        h: 'Operatorul de date',
        p: [
          `Aplicația Pnerp este operată în scopul gestionării registrului de parcele forestiere. Pentru orice întrebare legată de datele personale, ne puteți contacta la adresa ${CONTACT_EMAIL}.`,
        ],
      },
      {
        h: 'Ce date colectăm',
        ul: [
          'Date de cont: nume de utilizator, adresă de e-mail, nume complet, rol și limbă preferată.',
          'Date de localizare: coordonate GPS (latitudine, longitudine) și precizia acestora, atașate fotografiilor realizate pe teren.',
          'Fotografii și metadate: imaginile realizate pe teren împreună cu metadatele EXIF (data, ora, coordonatele).',
          'Date despre dispozitiv: modelul dispozitivului, versiunea aplicației și jetonul de notificări (push) necesar pentru trimiterea notificărilor.',
        ],
      },
      {
        h: 'Permisiuni solicitate',
        p: ['Aplicația solicită următoarele permisiuni pe dispozitivul dvs.:'],
        ul: [
          'Cameră: pentru a fotografia parcele și lucrări forestiere.',
          'Locație (GPS): pentru a atașa coordonatele geografice la fotografii.',
          'Galerie foto: pentru a salva fotografiile realizate pe teren.',
          'Microfon: solicitat de modulul camerei; aplicația nu înregistrează și nu stochează sunet.',
        ],
      },
      {
        h: 'Cum utilizăm datele',
        ul: [
          'Pentru a înregistra și gestiona parcelele forestiere și fotografiile georeferențiate.',
          'Pentru a asocia automat fotografiile cu parcela corespunzătoare, pe baza coordonatelor.',
          'Pentru autentificarea și gestionarea conturilor de utilizator.',
          'Pentru a trimite notificări legate de activitatea din aplicație.',
        ],
      },
      {
        h: 'Partajarea datelor',
        p: [
          'Nu vindem și nu partajăm datele dvs. personale cu terți în scopuri de marketing. Datele sunt stocate la furnizori de infrastructură care acționează ca persoane împuternicite (procesatori) și sunt utilizate exclusiv pentru funcționarea aplicației.',
        ],
      },
      {
        h: 'Stocare și securitate',
        p: [
          'Datele sunt stocate pe servere situate în Uniunea Europeană. Parolele sunt stocate în formă criptată (bcrypt). Accesul la date este restricționat în funcție de rolul utilizatorului.',
        ],
      },
      {
        h: 'Păstrarea datelor',
        p: [
          'Înregistrările șterse sunt păstrate o perioadă de recuperare de 30 de zile, după care pot fi eliminate definitiv. Datele de cont sunt păstrate cât timp contul este activ.',
        ],
      },
      {
        h: 'Drepturile dvs.',
        p: [
          `Aveți dreptul de a solicita accesul la datele dvs. personale, corectarea sau ștergerea acestora. Pentru a vă exercita aceste drepturi, contactați-ne la ${CONTACT_EMAIL}.`,
        ],
      },
      {
        h: 'Copii',
        p: [
          'Aplicația este destinată exclusiv personalului serviciului silvic și nu se adresează copiilor sub 16 ani.',
        ],
      },
      {
        h: 'Modificări ale politicii',
        p: [
          'Putem actualiza această politică periodic. Versiunea actualizată va fi publicată la această adresă, împreună cu data ultimei modificări.',
        ],
      },
      {
        h: 'Contact',
        p: [
          `Pentru orice întrebări privind această politică de confidențialitate, ne puteți contacta la: ${CONTACT_EMAIL}.`,
        ],
      },
    ],
  },
  ru: {
    title: 'Политика конфиденциальности',
    updated: 'Последнее обновление: 8 июля 2026 г.',
    intro:
      'Настоящая политика конфиденциальности описывает, как приложение Pnerp («приложение») собирает, использует и защищает персональные данные пользователей. Приложение является внутренним инструментом для сотрудников государственной лесной службы Республики Молдова, предназначенным для регистрации и управления лесными участками и геопривязанными фотографиями.',
    sections: [
      {
        h: 'Оператор данных',
        p: [
          `Приложение Pnerp используется для ведения реестра лесных участков. По всем вопросам, связанным с персональными данными, вы можете связаться с нами по адресу ${CONTACT_EMAIL}.`,
        ],
      },
      {
        h: 'Какие данные мы собираем',
        ul: [
          'Данные учётной записи: имя пользователя, адрес электронной почты, полное имя, роль и предпочитаемый язык.',
          'Данные о местоположении: координаты GPS (широта, долгота) и их точность, прикрепляемые к фотографиям, сделанным на местности.',
          'Фотографии и метаданные: изображения, снятые на местности, вместе с метаданными EXIF (дата, время, координаты).',
          'Данные об устройстве: модель устройства, версия приложения и токен push-уведомлений, необходимый для отправки уведомлений.',
        ],
      },
      {
        h: 'Запрашиваемые разрешения',
        p: ['Приложение запрашивает следующие разрешения на вашем устройстве:'],
        ul: [
          'Камера: для фотографирования участков и лесных работ.',
          'Местоположение (GPS): для прикрепления географических координат к фотографиям.',
          'Фотогалерея: для сохранения сделанных фотографий.',
          'Микрофон: запрашивается модулем камеры; приложение не записывает и не хранит звук.',
        ],
      },
      {
        h: 'Как мы используем данные',
        ul: [
          'Для регистрации и управления лесными участками и геопривязанными фотографиями.',
          'Для автоматической привязки фотографий к соответствующему участку на основе координат.',
          'Для аутентификации и управления учётными записями пользователей.',
          'Для отправки уведомлений, связанных с активностью в приложении.',
        ],
      },
      {
        h: 'Передача данных',
        p: [
          'Мы не продаём и не передаём ваши персональные данные третьим лицам в маркетинговых целях. Данные хранятся у поставщиков инфраструктуры, действующих в качестве обработчиков, и используются исключительно для работы приложения.',
        ],
      },
      {
        h: 'Хранение и безопасность',
        p: [
          'Данные хранятся на серверах в Европейском союзе. Пароли хранятся в зашифрованном виде (bcrypt). Доступ к данным ограничен в зависимости от роли пользователя.',
        ],
      },
      {
        h: 'Срок хранения данных',
        p: [
          'Удалённые записи хранятся в течение периода восстановления 30 дней, после чего могут быть удалены окончательно. Данные учётной записи хранятся, пока учётная запись активна.',
        ],
      },
      {
        h: 'Ваши права',
        p: [
          `Вы имеете право запросить доступ к своим персональным данным, их исправление или удаление. Чтобы воспользоваться этими правами, свяжитесь с нами по адресу ${CONTACT_EMAIL}.`,
        ],
      },
      {
        h: 'Дети',
        p: [
          'Приложение предназначено исключительно для сотрудников лесной службы и не адресовано детям младше 16 лет.',
        ],
      },
      {
        h: 'Изменения политики',
        p: [
          'Мы можем периодически обновлять настоящую политику. Обновлённая версия будет опубликована по этому адресу с указанием даты последнего изменения.',
        ],
      },
      {
        h: 'Контакты',
        p: [
          `По любым вопросам, касающимся настоящей политики конфиденциальности, вы можете связаться с нами: ${CONTACT_EMAIL}.`,
        ],
      },
    ],
  },
  en: {
    title: 'Privacy Policy',
    updated: 'Last updated: 8 July 2026',
    intro:
      'This privacy policy describes how the Pnerp application ("the app") collects, uses and protects users\' personal data. The app is an internal tool for the staff of the state forestry service of the Republic of Moldova, used to register and manage forest parcels and georeferenced photos.',
    sections: [
      {
        h: 'Data controller',
        p: [
          `The Pnerp app is operated for the purpose of maintaining the forest parcel registry. For any questions regarding personal data, you can contact us at ${CONTACT_EMAIL}.`,
        ],
      },
      {
        h: 'What data we collect',
        ul: [
          'Account data: username, email address, full name, role and preferred language.',
          'Location data: GPS coordinates (latitude, longitude) and their accuracy, attached to photos taken in the field.',
          'Photos and metadata: images taken in the field along with EXIF metadata (date, time, coordinates).',
          'Device data: device model, app version and the push notification token required to send notifications.',
        ],
      },
      {
        h: 'Permissions requested',
        p: ['The app requests the following permissions on your device:'],
        ul: [
          'Camera: to photograph parcels and forestry work.',
          'Location (GPS): to attach geographic coordinates to photos.',
          'Photo library: to save the photos taken in the field.',
          'Microphone: requested by the camera module; the app does not record or store audio.',
        ],
      },
      {
        h: 'How we use the data',
        ul: [
          'To register and manage forest parcels and georeferenced photos.',
          'To automatically associate photos with the corresponding parcel, based on coordinates.',
          'To authenticate and manage user accounts.',
          'To send notifications related to activity in the app.',
        ],
      },
      {
        h: 'Data sharing',
        p: [
          'We do not sell or share your personal data with third parties for marketing purposes. Data is stored with infrastructure providers acting as processors and is used solely to operate the app.',
        ],
      },
      {
        h: 'Storage and security',
        p: [
          'Data is stored on servers located in the European Union. Passwords are stored in encrypted form (bcrypt). Access to data is restricted based on the user\'s role.',
        ],
      },
      {
        h: 'Data retention',
        p: [
          'Deleted records are kept for a 30-day recovery period, after which they may be permanently removed. Account data is kept for as long as the account is active.',
        ],
      },
      {
        h: 'Your rights',
        p: [
          `You have the right to request access to your personal data, its correction or deletion. To exercise these rights, contact us at ${CONTACT_EMAIL}.`,
        ],
      },
      {
        h: 'Children',
        p: [
          'The app is intended solely for forestry service staff and is not directed at children under 16.',
        ],
      },
      {
        h: 'Changes to this policy',
        p: [
          'We may update this policy from time to time. The updated version will be published at this address, with the date of the last change.',
        ],
      },
      {
        h: 'Contact',
        p: [
          `For any questions regarding this privacy policy, you can contact us at: ${CONTACT_EMAIL}.`,
        ],
      },
    ],
  },
};

const LANGS: { key: Lang; label: string }[] = [
  { key: 'ro', label: 'RO' },
  { key: 'ru', label: 'RU' },
  { key: 'en', label: 'EN' },
];

const isLang = (v: string | null): v is Lang => v === 'ro' || v === 'ru' || v === 'en';

export function PrivacyPage() {
  const { i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const param = searchParams.get('lang');
  const lang: Lang = isLang(param)
    ? param
    : (['ro', 'ru', 'en'] as Lang[]).find((l) => i18n.language?.startsWith(l)) ?? 'ro';
  const setLang = (l: Lang) => setSearchParams({ lang: l }, { replace: true });
  const doc = CONTENT[lang];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <header className="border-b border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4">
          <Link to="/login" className="text-lg font-semibold text-forest-700 dark:text-forest-500">
            Pnerp
          </Link>
          <div className="flex gap-1">
            {LANGS.map((l) => (
              <button
                key={l.key}
                type="button"
                onClick={() => setLang(l.key)}
                className={`rounded px-2.5 py-1 text-sm font-medium transition ${
                  lang === l.key
                    ? 'bg-forest-700 text-white'
                    : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{doc.title}</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{doc.updated}</p>
        <p className="mt-4 text-slate-700 dark:text-slate-300">{doc.intro}</p>

        <div className="mt-6 space-y-6">
          {doc.sections.map((s) => (
            <section key={s.h}>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{s.h}</h2>
              {s.p?.map((para, i) => (
                <p key={i} className="mt-2 text-slate-700 dark:text-slate-300">
                  {para}
                </p>
              ))}
              {s.ul && (
                <ul className="mt-2 list-disc space-y-1 pl-5 text-slate-700 dark:text-slate-300">
                  {s.ul.map((li, i) => (
                    <li key={i}>{li}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <footer className="mt-10 border-t border-slate-200 pt-4 text-sm text-slate-500 dark:border-slate-800 dark:text-slate-400">
          <Link to="/login" className="text-forest-700 hover:underline dark:text-forest-500">
            ← Pnerp
          </Link>
        </footer>
      </main>
    </div>
  );
}
